import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAndroid, isIOS, isPWAInstalled } from 'pwa/platform';

declare global {
	interface BeforeInstallPromptEvent extends Event {
		readonly platforms: string[];
		prompt: () => Promise<void>;
		userChoice: Promise<{
			outcome: 'accepted' | 'dismissed';
			platform: string;
		}>;
	}
}

const DISMISS_KEY = 'pwaInstallDismissedAt';
const DISMISS_COOLDOWN_DAYS = 7; // don't nag users too often

function getDismissedRecently() {
	try {
		const raw = localStorage.getItem(DISMISS_KEY);
		if (!raw) return false;
		const ts = Number(raw);
		if (!Number.isFinite(ts)) return false;
		const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
		return days < DISMISS_COOLDOWN_DAYS;
	} catch {
		return false;
	}
}

export type InstallState = {
	installed: boolean;
	canInstall: boolean; // Android/Chrome only when event fired
	shouldNudge: boolean; // iOS when not installed or Android when event ready
	platform: 'ios' | 'android' | 'other';
	promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
	dismiss: () => void;
};

export function usePWAInstall(): InstallState {
	const [installed, setInstalled] = useState(isPWAInstalled());
	const [canInstall, setCanInstall] = useState(false);
	const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);
	const [dismissedRecently, setDismissedRecently] = useState(
		getDismissedRecently(),
	);

	const platform = useMemo(
		() => (isIOS() ? 'ios' : isAndroid() ? 'android' : 'other'),
		[],
	);

	useEffect(() => {
		const onBIP = (e: Event) => {
			const ev = e as BeforeInstallPromptEvent;
			ev.preventDefault();
			deferredRef.current = ev;
			setCanInstall(true);
		};
		const onInstalled = () => {
			setInstalled(true);
			deferredRef.current = null;
			setCanInstall(false);
		};

		window.addEventListener('beforeinstallprompt', onBIP);
		window.addEventListener('appinstalled', onInstalled);

		return () => {
			window.removeEventListener('beforeinstallprompt', onBIP);
			window.removeEventListener('appinstalled', onInstalled);
		};
	}, []);

	const promptInstall = useCallback(async () => {
		if (installed) return 'unavailable';
		if (platform === 'android' && deferredRef.current) {
			try {
				await deferredRef.current.prompt();
				const { outcome } = await deferredRef.current.userChoice;
				deferredRef.current = null; // event may only be used once
				setCanInstall(false);
				return outcome;
			} catch {
				return 'dismissed';
			}
		}
		return 'unavailable';
	}, [installed, platform]);

	const dismiss = useCallback(() => {
		try {
			localStorage.setItem(DISMISS_KEY, String(Date.now()));
			setDismissedRecently(true);
		} catch {}
	}, []);

	const shouldNudge = useMemo(() => {
		if (installed) return false;
		if (dismissedRecently) return false;
		if (platform === 'ios') return true;
		if (platform === 'android') return canInstall;
		return false;
	}, [installed, dismissedRecently, platform, canInstall]);

	return {
		installed,
		canInstall,
		shouldNudge,
		platform,
		promptInstall,
		dismiss,
	};
}
